import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { usePersona } from '@/app/providers';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import {
  INITIAL_ESCALATION_DECISIONS,
  recordEscalationDecision,
  type EscalationDecisionState,
} from './EscalationDecisionEngine';

type EscalationDecisionContextValue = {
  records: EscalationDecisionState;
  /** Records the decision on a card at the session cursor, as the current persona. */
  record: (cardId: string, decision: string) => void;
};

const EscalationDecisionContext = createContext<EscalationDecisionContextValue | null>(null);

/**
 * Project-level holder of the manager's escalation decisions (K12e round 2),
 * mounted beside `AdvisoryProvider` so a record outlives the GM landing's
 * mount: leaving for Advisory and coming back keeps the gate recorded. Only a
 * reload resets it.
 */
export function EscalationDecisionProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState<EscalationDecisionState>(INITIAL_ESCALATION_DECISIONS);
  const { persona } = usePersona();
  const { state } = useScenario();
  const record = useCallback(
    (cardId: string, decision: string) =>
      setRecords((current) =>
        recordEscalationDecision(current, {
          cardId,
          decision,
          recordedAt: state.cursor,
          recordedBy: persona,
        }),
      ),
    [persona, state.cursor],
  );
  const value = useMemo(() => ({ records, record }), [records, record]);
  return <EscalationDecisionContext.Provider value={value}>{children}</EscalationDecisionContext.Provider>;
}

export function useEscalationDecisions(): EscalationDecisionContextValue {
  const ctx = useContext(EscalationDecisionContext);
  if (!ctx) throw new Error('useEscalationDecisions must be used inside <EscalationDecisionProvider>');
  return ctx;
}
